import { Injectable } from "@angular/core";
import { Observable } from "rxjs/internal/Observable";
import { TranslateService } from "@ngx-translate/core";
import { map, switchMap, tap } from "rxjs/operators";
import { Addon, AddonType, IStmxHost, TabKeyEnum, IStmxGroup } from "ngx-exdr-stmx-common";
import { from, of } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class StmxGroupService implements IStmxGroup {
  constructor(
    private IStmxHostService: IStmxHost,    
    private translate: TranslateService
  ) { }    

  getTabTranslation(key: TabKeyEnum | string): Observable<string> {
    return this.IStmxHostService.getAddons().pipe(
      switchMap((addons: Addon[]) => {        
        return this._getTabTranslation(key, addons);
      })
    );
  }


  private _getTabTranslation(tabKey: string, addons: Addon[]): Observable<string> {
    let title = '';
    if (!tabKey) {
      return of(title);
    }

    return this.IStmxHostService.getHeaderTabsFiltered({ key: tabKey }).pipe(
      switchMap((tabs: any[]) => {
        if (tabs?.length) {
          title = tabs[0].name; 
        } else {
          let customTab = addons.find(
            (addon) => addon.InstanceId === tabKey.replace('custom tab', '')
          );
          if (!customTab) {
            customTab = addons.find(
              (addon) => addon.InstanceId === tabKey.replace('footer tab', '')
            );
            if (!customTab) {
              customTab = addons.find(
                (addon) => addon?.PublicProperties?.urlName === tabKey
              );
            }
          }
          if (customTab) {
            title = customTab.PublicProperties?.name ?? title;
          }
        }

        const tabOrderAddon = addons.find(
          (addon) =>
            addon.Type.toLowerCase() === AddonType.TabOrder.toLowerCase()
        );
        let tabOrderItems =
          tabOrderAddon?.PublicProperties?.jsonData?.data || [];
        const utilityTabOrderItems =
          tabOrderAddon?.PublicProperties?.jsonData?.utilities || [];
        const footerTabOrderItems =
          tabOrderAddon?.PublicProperties?.jsonData?.footer || [];
        tabOrderItems = tabOrderItems
          .concat(utilityTabOrderItems)
          .concat(footerTabOrderItems);
        const itemOrder = tabOrderItems.find((t) => t.key === tabKey);
        if (itemOrder) {
          title = itemOrder?.name;
        }

        const langAddon = addons.find(
          (a) => a.Type.toLowerCase() === AddonType.GroupLanguages.toLowerCase()
        );
        const langData = langAddon?.PublicProperties?.jsonData;
        const locales = Object.keys(langData ?? {});
        if (!locales?.length) {
          return of(title);
        }

        const currentLang = langData[this.IStmxHostService.getTranslateLocale()];
        if (currentLang?.settings?.isEnable === '1') {
          let key = tabKey.replace('custom tab', '');
          if (tabKey.startsWith('footer')) {
            key = tabKey.replace('footer tab', '');
          }
          const tabData = currentLang[key];
          if (tabData) {
            title = tabData.name?.length ? tabData.name : title;
          }
        }

        return of(title); // Wrap final result in an observable
      })
    );
  }


  getLocaleData(locale: string) {        
    return this.IStmxHostService.getAddons().pipe(
      map((addons: Addon[]) => {
        const langAddon = addons.find(
          (a) => a.Type.toLowerCase() == AddonType.GroupLanguages.toLowerCase()
        );
        let langData = langAddon?.PublicProperties?.jsonData;
        const locales = Object.keys(langData ?? {});
        if (!locales?.length || !locales?.includes(locale)) {
          return null;
        }
        return langData[this.IStmxHostService.getTranslateLocale()] ?? null;
      })
    );
  }

  getLanguageData(languageDropDownDisplay) {        
    return this.IStmxHostService.getAddons().pipe(
      map((addons: Addon[]) => {
        const groupLanguageAddon = addons.find(
          (addon) => addon.Type === AddonType.GroupLanguages
        );
        if (groupLanguageAddon) {
          let langData = groupLanguageAddon?.PublicProperties?.jsonData;
          let languagesArray = Object.keys(langData).map(function (
            personNamedIndex
          ) {
            let person = langData[personNamedIndex];
            return person;
          });
          if (languagesArray.filter(la => la.settings?.isEnable === '1').length > 1) {
            languageDropDownDisplay = true;
          }
        }
        return languageDropDownDisplay;
      })
    );
  }

  getTabLocaleData(tabKey: string, locale: string) {
    return this.getLocaleData(locale).pipe(map(localeData => {
      if (!localeData) { return null }
      return localeData[tabKey] ?? null;
    }))
  }
}
